import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, receiptUrl, receiptPreviewUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHelpPanel } from "@/components/PageHelpPanel";
import { Card, Btn, PageHeader, EmptyState, InfoTip } from "@/components/common";
import { fmtDate, CURRENCY_FIELD_HELP, nameOf, SUBMISSION_STATUS_LABEL, UNRECOGNISED_WORD, money } from "@/lib/format";
import { CurrencySelect } from "@/components/CurrencySelect";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import { useParties } from "@/lib/parties";
import { usePaymentSources, psLabel, psOwnerName } from "@/lib/payments";
import { DeleteDialog } from "@/components/DeleteDialog";
import { DateConfirmBanner, dateFlagMessage, useDateConfirm } from "@/components/DateConfirmBanner";
import { toast } from "sonner";
import {
  Camera, UploadSimple, PaperPlaneTilt, ArrowRight, CheckCircle, Warning,
  ClockCounterClockwise, X as XIcon, Receipt, Eye, FilePdf, Image as ImageIcon,
  Trash, CaretRight, CaretDown,
} from "@phosphor-icons/react";
import { RECEIPT_ACCEPT, RECEIPT_HINT, hasAcceptedExtension, readingFoundNothing, READING_FOUND_NOTHING, readAmount } from "@/lib/uploads";
import { MoneyInput } from "@/lib/accountingFormat";
import { curCode } from "@/lib/currencies";

// THE WORDS MOVED, THE COLOURS STAYED. (ZEN-635.) What a beneficiary READS is
// now declared once in lib/format.js and pinned to the server's own list, so a
// word the server can write and this screen cannot name fails the build instead
// of reaching a student. The colours are presentation and belong to this page;
// a missing colour shows a neutral chip, which is not a lie about anything.
const STATUS_COLOR = {
  needs_student_review: "bg-[#D97706]/10 text-[#92400E]",
  awaiting_owner_approval: "bg-[#034AA2]/10 text-[#034AA2]",
  approved: "bg-[#077A39]/10 text-[#077A39]",
  returned: "bg-[#F01919]/10 text-[#F01919]",
  rejected_by_owner: "bg-[#6b7280]/10 text-[#6b7280]",
  cancelled_by_student: "bg-[#E9E9EB] text-[#6b7280]",
};
const NEUTRAL_CHIP = "bg-[#E9E9EB] text-[#6b7280]";

export default function MyReceipts() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ["my-submissions"],
    queryFn: async () => (await api.get("/submissions/mine")).data,
  });
  // (The beneficiary's expense group was fetched here only to scope the category
  // picker. The picker is gone — a beneficiary submits facts, the account owner
  // decides what the expense is. ZEN-114)

  const needsReview = receipts.filter(r => r.submission_status === "needs_student_review");
  const returned = receipts.filter(r => r.submission_status === "returned");
  const awaiting = receipts.filter(r => r.submission_status === "awaiting_owner_approval");
  const approved = receipts.filter(r => r.submission_status === "approved");
  const rejected = receipts.filter(r => r.submission_status === "rejected_by_owner");

  const onUpload = async (files) => {
    setUploading(true);
    let count = 0;
    for (const file of Array.from(files)) {
      // Never skip in silence. A student photographs a receipt, drops it in,
      // and a hardcoded list decided it did not count — no message, no file,
      // no way to tell. If we cannot take it, say so.
      if (!hasAcceptedExtension(file.name)) {
        toast.error(`${file.name} is not a photo or PDF, so it was not uploaded.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("run_ai", "true");
        await api.post("/receipts/upload", fd);
        count++;
      } catch (e) {
        const msg = apiErr(e);
        if (msg.includes("Duplicate")) toast.error(`${file.name}: already uploaded`);
        else toast.error(`${file.name}: ${msg}`);
      }
    }
    if (count > 0) {
      toast.success(`${count} receipt${count > 1 ? "s" : ""} uploaded`);
      qc.invalidateQueries({ queryKey: ["my-submissions"] });
    }
    setUploading(false);
  };

  return (
    <div>
      <PageHeader title="My Receipts" subtitle="Upload receipts, review AI readings, and submit for approval." />
      <PageHelpPanel pageKey="receipts" />

      {/* Section A: Upload */}
      <Card className="mt-4" data-testid="student-upload-section">
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-[#034AA2]">
          <Camera size={16} weight="bold" className="mr-1.5 inline -mt-0.5" /> Add a Receipt
        </h3>
        <div
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#034AA2]", "bg-[#034AA2]/10"); }}
          onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-[#034AA2]", "bg-[#034AA2]/10"); }}
          onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-[#034AA2]", "bg-[#034AA2]/10"); onUpload(e.dataTransfer.files); }}
          className="rounded-xl border-2 border-dashed border-[#C2C9D1] bg-[#F8FAFC] p-5 text-center transition-colors"
          data-testid="student-receipt-dropzone"
        >
          <UploadSimple size={28} weight="bold" className="mx-auto text-[#034AA2]" />
          <div className="mt-1.5 text-sm font-bold text-[#1f2937]">
            {uploading ? "Uploading…" : "Drop receipt files here"}
          </div>
          <p className="mt-0.5 text-xs text-[#6b7280]">{RECEIPT_HINT}</p>
          <div className="mx-auto mt-2.5 flex max-w-xs items-start gap-1.5 rounded-lg border border-[#D97706]/40 bg-[#FCF5EE] px-2.5 py-1.5 text-left" data-testid="student-one-receipt-note">
            <Receipt size={14} weight="bold" className="mt-0.5 shrink-0 text-[#92600A]" />
            <p className="text-[10.5px] leading-snug text-[#92600A]">
              <b>One receipt per photo.</b> Several paper receipts? Take a separate photo of each one.
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <Btn onClick={() => fileRef.current?.click()} data-testid="upload-file-btn"><UploadSimple size={16} weight="bold" /> Upload file</Btn>
            <Btn variant="green" onClick={() => camRef.current?.click()} data-testid="camera-btn"><Camera size={16} weight="bold" /> Take photo</Btn>
          </div>
          <input ref={fileRef} type="file" accept={RECEIPT_ACCEPT} multiple className="hidden"
            onChange={(e) => { if (e.target.files) onUpload(e.target.files); e.target.value = ""; }}
            data-testid="student-receipt-input" />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { if (e.target.files) onUpload(e.target.files); e.target.value = ""; }}
            data-testid="student-camera-input" />
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-[#6b7280]" data-testid="long-receipt-guidance">
          Long receipt, like groceries? Use your phone&rsquo;s Scan Documents (iPhone Notes or Files, Android Google Drive) to make one PDF, then upload it.
        </p>
      </Card>

      {/* Section B: Needs your review */}
      {(needsReview.length > 0 || returned.length > 0) && (
        <div className="mt-6" data-testid="student-review-section">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]">
            Needs your review ({needsReview.length + returned.length})
          </h3>
          <ReviewList items={[...returned, ...needsReview]}
            onSubmitted={() => qc.invalidateQueries({ queryKey: ["my-submissions"] })} />
        </div>
      )}

      {/* Section C: My submissions */}
      {(awaiting.length > 0 || approved.length > 0 || rejected.length > 0) && (
        <div className="mt-6" data-testid="student-submissions-section">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]">My Submissions</h3>
          <PaginatedSubmissions items={[...awaiting, ...approved, ...rejected]}
            onCancelled={() => qc.invalidateQueries({ queryKey: ["my-submissions"] })} />
        </div>
      )}

      {!isLoading && receipts.length === 0 && (
        <EmptyState icon={Receipt} title="No receipts yet" subtitle="Upload your first receipt above and it'll appear here for review." />
      )}
    </div>
  );
}


const PAGE_SIZE = 20;

function ReviewList({ items, onSubmitted }) {
  const [expandedId, setExpandedId] = useState(null);
  const [shown, setShown] = useState(PAGE_SIZE);
  const visible = items.slice(0, shown);
  return (
    <div className="space-y-2">
      {visible.map(r => (
        expandedId === r.id ? (
          <ReceiptReviewCard key={r.id} receipt={r} onSubmitted={onSubmitted}
            onCollapse={() => setExpandedId(null)} />
        ) : (
          <ReviewSummaryRow key={r.id} receipt={r} onExpand={() => setExpandedId(r.id)} />
        )
      ))}
      {shown < items.length && (
        <button onClick={() => setShown(s => s + PAGE_SIZE)}
          className="mx-auto mt-2 block rounded-md border border-[#C2C9D1] px-4 py-2 text-xs font-semibold text-[#034AA2] hover:bg-[#E9E9EB] transition-colors" data-testid="review-show-more">
          Show more ({Math.min(PAGE_SIZE, items.length - shown)} of {items.length - shown} remaining)
        </button>
      )}
    </div>
  );
}

function ReviewSummaryRow({ receipt, onExpand }) {
  const draft = receipt.student_draft || receipt.draft || {};
  const isReturned = receipt.submission_status === "returned";
  return (
    <button onClick={onExpand}
      className={`flex w-full items-center gap-3 rounded-lg border bg-white px-3 py-2.5 text-left transition-colors hover:bg-[#E9E9EB]/60 ${isReturned ? "border-[#F01919]/40" : "border-[#D97706]/40"}`}
      data-testid={`review-summary-${receipt.id}`}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#C2C9D1] bg-[#F8FAFC]">
        <img src={receiptUrl(receipt.id)} alt="" className="h-full w-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-[#1f2937] truncate">{draft.payee || receipt.original_filename || "Receipt"}</span>
          {isReturned && <span className="shrink-0 rounded-full bg-[#F01919]/10 px-1.5 py-0.5 text-[9px] font-bold text-[#F01919]">Returned</span>}
        </div>
        <div className="text-[11px] text-[#6b7280]">
          {draft.transaction_date && fmtDate(draft.transaction_date)}
          {readAmount(draft) != null && <> · {money(Number(readAmount(draft)), curCode(draft.original_currency, "USD"))}</>}
        </div>
      </div>
      <CaretRight size={16} weight="bold" className="shrink-0 text-[#6b7280]" />
    </button>
  );
}

function PaginatedSubmissions({ items, onCancelled }) {
  const [shown, setShown] = useState(PAGE_SIZE);
  const visible = items.slice(0, shown);
  return (
    <div className="space-y-2">
      {visible.map(r => <SubmissionRow key={r.id} receipt={r} onCancelled={onCancelled} />)}
      {shown < items.length && (
        <button onClick={() => setShown(s => s + PAGE_SIZE)}
          className="mx-auto mt-2 block rounded-md border border-[#C2C9D1] px-4 py-2 text-xs font-semibold text-[#034AA2] hover:bg-[#E9E9EB] transition-colors" data-testid="submissions-show-more">
          Show more ({Math.min(PAGE_SIZE, items.length - shown)} of {items.length - shown} remaining)
        </button>
      )}
    </div>
  );
}

function ReceiptReviewCard({ receipt, onSubmitted, onCollapse }) {
  const { user } = useAuth();
  const draft = receipt.draft || {};
  const existing = receipt.student_draft || draft;
  const isReturned = receipt.submission_status === "returned";
  const { data: parties } = useParties();
  const { data: psData } = usePaymentSources(false);
  const allSources = psData?.sources || [];
  // Filter to student's own payment sources
  const studentSources = allSources.filter(s => s.owner_type === "student" && s.student_id === user?.student_id);

  const [f, setF] = useState({
    transaction_date: existing.transaction_date || "",
    payee: existing.payee || "",
    description: existing.description || "",
    original_currency: curCode(existing.original_currency, "USD"),
    original_amount: readAmount(existing) ?? "",
    payor_type: existing.payor_type || "student",
    payor_party_id: existing.payor_party_id || "",
    payment_source_id: existing.payment_source_id || "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // ZEN-162: the server judged the prefilled date (draft_date_flag on the
  // receipt row); a save can also come back refused. Either way the amber
  // banner asks, and the tick is the only way past.
  const dc = useDateConfirm(receipt.draft_date_flag);
  React.useEffect(() => { dc.resetFor(receipt.id, receipt.draft_date_flag); }, [receipt.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const locked = busy || sent;
  const set = (k, v) => { if (!locked) setF(p => ({ ...p, [k]: v })); };

  const valid = f.transaction_date && f.payee && f.original_amount && f.original_currency && f.description && f.payor_type;

  const submit = async () => {
    if (!valid || locked) return;
    setBusy(true);
    try {
      await api.post(`/submissions/${receipt.id}/submit`, {
        ...f,
        original_amount: parseFloat(f.original_amount),
        payor_party_id: f.payor_type === "party" ? f.payor_party_id : null,
        payment_source_id: f.payment_source_id || null,
        date_confirmed: dc.confirmed,
      });
      setSent(true);
      toast.success("Sent for approval");
      onSubmitted();
    } catch (e) {
      setBusy(false);
      if (dc.applyRefusal(e)) {
        // banner shown; nothing else to say
      } else if (e?.response?.status === 409) {
        toast.info("This receipt was already submitted or processed — refreshing your list");
        onSubmitted();
      } else { toast.error(apiErr(e)); }
    }
  };

  return (
    <Card className={`${isReturned ? "border-[#F01919]/40" : "border-[#D97706]/40"}`} data-testid={`review-card-${receipt.id}`}>
      {onCollapse && (
        <button onClick={onCollapse} className="mb-2 flex items-center gap-1 text-[11px] font-semibold text-[#6b7280] hover:text-[#034AA2] transition-colors" data-testid={`review-collapse-${receipt.id}`}>
          <CaretDown size={12} weight="bold" /> Collapse
        </button>
      )}
      {isReturned && receipt.owner_note && (
        <div className="mb-3 rounded-md border border-[#F01919]/30 bg-[#F01919]/5 px-3 py-2" data-testid="return-note">
          <div className="text-xs font-bold text-[#F01919]">Returned by owner:</div>
          <div className="text-sm text-[#374151]">{receipt.owner_note}</div>
        </div>
      )}
      {receipt.ai_error && (
        <div className="mb-3 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-3 py-2 text-xs text-[#92400E]">
          We couldn&apos;t read this receipt — please fill it in manually.
        </div>
      )}
      {readingFoundNothing(receipt) && (
        <div className="mb-3 rounded-md border border-[#D97706]/30 bg-[#FEF3C7] px-3 py-2 text-xs text-[#92400E]" data-testid={`reading-found-nothing-${receipt.id}`}>
          {READING_FOUND_NOTHING}
        </div>
      )}
      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Receipt thumbnail */}
        <div className="shrink-0">
          <div className="h-32 w-32 overflow-hidden rounded-md border border-[#C2C9D1] bg-white cursor-pointer"
            onClick={() => setPreviewOpen(!previewOpen)}>
            <img src={receiptUrl(receipt.id)} alt="" className="h-full w-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }} />
          </div>
          <button onClick={() => setPreviewOpen(!previewOpen)} className="mt-1 text-[11px] font-semibold text-[#034AA2] hover:underline">
            <Eye size={12} weight="bold" className="inline mr-0.5" /> {previewOpen ? "Hide" : "View full"}
          </button>
        </div>
        {/* Form */}
        <div className={`flex-1 grid grid-cols-1 gap-2 sm:grid-cols-2 ${locked ? "pointer-events-none opacity-60" : ""}`}>
          <label className="block text-xs font-semibold text-[#6b7280]">Date *
            <div className="mt-0.5"><UnifiedDateInput value={f.transaction_date} onChange={v => { set("transaction_date", v); dc.clear(); }} testid="sub-date" disabled={locked} /></div>
            <DateConfirmBanner message={dateFlagMessage(dc.flag, "student")}
              confirmed={dc.confirmed} onConfirmedChange={dc.setConfirmed}
              testid="sub-date-confirm" />
          </label>
          <label className="block text-xs font-semibold text-[#6b7280]">Payee / Vendor *
            <input value={f.payee} onChange={e => set("payee", e.target.value)} placeholder="e.g., University Bookstore"
              className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid="sub-payee" />
          </label>
          <label className="block text-xs font-semibold text-[#6b7280]"><span className="inline-flex items-center gap-1">Currency * <InfoTip testid="sub-currency-info" text={CURRENCY_FIELD_HELP} /></span>
            <div className="mt-0.5"><CurrencySelect value={f.original_currency} onChange={v => set("original_currency", v)} testid="sub-currency" className="!h-9" /></div>
          </label>
          <label className="block text-xs font-semibold text-[#6b7280]">Amount *
            <MoneyInput currency={curCode(f.original_currency)} className="mt-0.5 !h-9" value={f.original_amount} onChange={v => set("original_amount", v)}
              testid="sub-amount" />
          </label>
          <label className="col-span-1 sm:col-span-2 block text-xs font-semibold text-[#6b7280]">What was this for? *
            <input value={f.description} onChange={e => set("description", e.target.value)} placeholder="Brief description"
              className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid="sub-description" />
          </label>
          <div className="col-span-1 sm:col-span-2">
            <span className="text-xs font-semibold text-[#6b7280]">Who paid? *</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <button type="button" onClick={() => { set("payor_type", "student"); set("payor_party_id", ""); }}
                className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors ${f.payor_type === "student" ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}
                data-testid="sub-paidby-student">
                I paid
              </button>
              <button type="button" onClick={() => { set("payor_type", "owner"); set("payor_party_id", ""); set("payment_source_id", ""); }}
                className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors ${f.payor_type === "owner" ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}
                data-testid="sub-paidby-owner">
                {parties?.owner?.nickname || "Owner"} paid
              </button>
              {(parties?.custom || []).map(p => (
                <button key={p.id} type="button" onClick={() => { set("payor_type", "party"); set("payor_party_id", p.id); set("payment_source_id", ""); }}
                  className={`h-8 rounded-md px-3 text-xs font-semibold transition-colors ${f.payor_type === "party" && f.payor_party_id === p.id ? "bg-[#034AA2] text-white" : "border border-[#C2C9D1] bg-white hover:bg-[#E9E9EB]"}`}
                  data-testid={`sub-paidby-party-${p.id}`}>
                  {p.nickname} paid
                </button>
              ))}
            </div>
          </div>
          {f.payor_type === "student" && studentSources.length > 0 && (
            <label className="block text-xs font-semibold text-[#6b7280]">Paid from
              <select value={f.payment_source_id} onChange={e => set("payment_source_id", e.target.value)}
                className="mt-0.5 block h-9 w-full rounded-md border border-[#C2C9D1] px-2 text-sm" data-testid="sub-payment-source">
                <option value="">— Not sure / cash —</option>
                {studentSources.map(s => <option key={s.id} value={s.id}>{psLabel(s, psOwnerName(s, [{id: user.student_id, full_name: user.full_name || ""}], parties))}</option>)}
              </select>
            </label>
          )}
        </div>
      </div>
      {previewOpen && (
        <div className="mt-3 border-t border-[#C2C9D1] pt-3">
          <ReceiptViewer key={receipt.id} getPageSrc={(page) => receiptPreviewUrl(receipt.id, page)} pageCount={receipt.page_count || 1} />
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <button type="button" onClick={() => setCancelOpen(true)} disabled={locked}
          className={`flex items-center gap-1 text-xs font-semibold transition-colors ${locked ? "text-[#C2C9D1] cursor-not-allowed" : "text-[#6b7280] hover:text-[#F01919]"}`} data-testid={`sub-remove-${receipt.id}`}>
          <Trash size={14} weight="bold" /> Remove
        </button>
        {sent ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[#059669]/10 px-4 py-2 text-sm font-bold text-[#059669]" data-testid={`sub-sent-${receipt.id}`}>
            <CheckCircle size={18} weight="bold" /> Sent
          </span>
        ) : (
          <Btn onClick={submit} disabled={!valid || locked} data-testid={`sub-submit-${receipt.id}`}>
            {busy ? "Submitting…" : <><PaperPlaneTilt size={16} weight="bold" /> {isReturned ? "Resubmit" : "Submit for Approval"}</>}
          </Btn>
        )}
      </div>
      <CancelDialog open={cancelOpen} onClose={() => setCancelOpen(false)} receiptId={receipt.id}
        onDone={onSubmitted} />
    </Card>
  );
}


function SubmissionRow({ receipt, onCancelled }) {
  // THIS LINE IS THE TICKET. It used to read
  //   STATUS_LABELS[receipt.submission_status] || { label: receipt.submission_status, ... }
  // so a word the table did not know was shown to the beneficiary as itself —
  // `awaiting_owner_approval` where "With owner" belongs. On their OWN screen.
  const st = {
    label: nameOf(SUBMISSION_STATUS_LABEL, receipt.submission_status,
                  UNRECOGNISED_WORD),
    color: nameOf(STATUS_COLOR, receipt.submission_status, NEUTRAL_CHIP),
  };
  const draft = receipt.student_draft || receipt.draft || {};
  const { data: parties } = useParties();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);
  const canWithdraw = receipt.submission_status === "awaiting_owner_approval" && !withdrawn;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#C2C9D1] bg-white px-3 py-2" data-testid={`submission-${receipt.id}`}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#C2C9D1] bg-[#F8FAFC]">
        <img src={receiptUrl(receipt.id)} alt="" className="h-full w-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-[#1f2937]">{draft.payee || receipt.original_filename}</span>
        </div>
        <div className="text-[11px] text-[#6b7280]">
          {draft.transaction_date && fmtDate(draft.transaction_date)}
          {readAmount(draft) != null && <> · {money(Number(readAmount(draft)), curCode(draft.original_currency, "USD"))}</>}
        </div>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${st.color}`}>{st.label}</span>
      {receipt.submission_status === "rejected_by_owner" && receipt.owner_note && (
        <span className="text-[11px] text-[#F01919]" title={receipt.owner_note}>Reason: {receipt.owner_note}</span>
      )}
      {canWithdraw && (
        <button onClick={() => setCancelOpen(true)}
          className="shrink-0 text-[11px] font-semibold text-[#6b7280] hover:text-[#F01919] transition-colors" data-testid={`sub-withdraw-${receipt.id}`}>
          Withdraw
        </button>
      )}
      {withdrawn && (
        <span className="shrink-0 text-[11px] font-semibold text-[#059669]" data-testid={`sub-withdrawn-${receipt.id}`}>
          Withdrawn
        </span>
      )}
      <CancelDialog open={cancelOpen} onClose={() => setCancelOpen(false)} receiptId={receipt.id}
        onDone={() => { setWithdrawn(true); onCancelled?.(); }} />
    </div>
  );
}


function CancelDialog({ open, onClose, receiptId, onDone }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/submissions/${receiptId}/cancel`);
      toast.success("Submission removed");
      onClose();
      onDone?.();
    } catch (e) {
      setBusy(false);
      if (e?.response?.status === 409) {
        toast.info("This receipt was already submitted or processed — refreshing your list");
        onClose();
        onDone?.();
      } else { toast.error(apiErr(e)); }
    }
  };
  return <DeleteDialog open={open} onClose={onClose} onConfirm={confirm} noun="submission" busy={busy} testid="cancel-receipt" />;
}
